import { resolveNoContent, resolveWithBody } from "./Utils/utils";
import { usePromisedRequest as promiseRequest } from "../Common/request";
import { BracketTypeConstraint, BracketTypeSettings } from "../Brackets/configurationTemplates";
import { ScoreboardType } from "../Scoreboard/Common/types";
import { BracketType } from "../Common/types";

export type DisciplineType =
    | "bjj"
    | "submissionFighting"
    | "mma"
    | "combatJuJutsu"
    | "wrestling"
    | "grappling"
    | "judo"
    | "kata"
    | "aikido"
    | "sambo"
    | "kickboxing"
    | "k1"
    | "boxing"
    | "muayThai"
    | "taekwondo"
    | "karate"
    | "juJitsu"
    | "fighting"
    | "newaza";

export type MmaBasicScoreboardSettings = {
    numberOfRounds?: number | null;
    breakBetweenRounds?: string | null;
};

export type KataCalculationMethod = "sum" | "average";

export type KataScoreboardSettings = {
    numberOfJudges?: number;
    showTimer?: boolean;
    discardMinMax?: boolean;
    calculationMethod?: KataCalculationMethod;
    showFullClassificationAfterPerformance?: boolean;
};

export type ScoreboardSettings = KataScoreboardSettings & MmaBasicScoreboardSettings;

export type DisciplinePackageOverrideResponse = {
    id: number;
    name: string | null;
    bracketConstraints: BracketTypeConstraint[] | null;
    bracketTypeSettings: BracketTypeSettings | null;
    parameterIds: number[];
    scoreboardType: ScoreboardType | null;
    scoreboardSettings: ScoreboardSettings | null;
};

export type EligibleCategory = { id: number; path: string };

export type ConfiguredDisciplinePackage = {
    discipline: DisciplineType;
    isConfigured: boolean;
    scoreboardType: ScoreboardType;
    scoreboardSettings: ScoreboardSettings;
    bracketConstraints: BracketTypeConstraint[];
    bracketTypeSettings: BracketTypeSettings | null;
    parameterOverrides: DisciplinePackageOverrideResponse[];
    isOrphaned?: boolean;
};

export type DisciplinePackagesResponse = {
    disciplines: ConfiguredDisciplinePackage[];
    //It shows how many categories from categories tree is not covered with discipline. Either directly or via inheritance.
    uncoveredCategories: number;
};

export type UpdateDisciplinePackageRequest = {
    scoreboardType: ScoreboardType;
    scoreboardSettings: ScoreboardSettings;
    bracketConstraints: BracketTypeConstraint[];
    bracketTypeSettings: BracketTypeSettings;
    parameterOverrides: Omit<DisciplinePackageOverrideResponse, "id">[];
};

export type DisciplineMetadata = {
    /** All scoreboard type identifiers available for this discipline. */
    scoreboardTypes: ScoreboardType[];
    /** Ordered bracket type values available for this discipline. */
    allowedBracketTypes: BracketType[];
    /** Bracket type used for competitor counts not covered by any configured range. */
    defaultBracketType: BracketType;
    /** Default configuration used when the package has not been saved yet. */
    defaults: {
        scoreboardType: ScoreboardType;
    };
};

export type DisciplineMetadataMap = { [key in DisciplineType]: DisciplineMetadata };

/** Configurable setting defaults per scoreboard type, keyed by type identifier. */
export type ScoreboardTypeSettingsMap = Record<string, ScoreboardSettings>;

export type DisciplineMetadataResponse = {
    disciplines: DisciplineMetadataMap;
    scoreboardSettings: ScoreboardTypeSettingsMap;
};

export default {
    getDisciplinePackages(eventId: number): Promise<DisciplinePackagesResponse> {
        return promiseRequest<DisciplinePackagesResponse>({
            url: `/api/admin/events/${eventId}/discipline-packages`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },

    updateDisciplinePackage(eventId: number, discipline: DisciplineType, request: UpdateDisciplinePackageRequest): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/discipline-packages/${discipline}`,
            method: "put",
            json: request,
        }).then(resolveNoContent());
    },

    deleteDisciplinePackage(eventId: number, discipline: DisciplineType): Promise<void> {
        return promiseRequest({
            url: `/api/admin/events/${eventId}/discipline-packages/${discipline}`,
            method: "delete",
        }).then(resolveNoContent());
    },

    getDisciplineMetadata(): Promise<DisciplineMetadataResponse> {
        return promiseRequest<DisciplineMetadataResponse>({
            url: `/api/admin/discipline-packages/metadata`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },

    getEligibleCategories(eventId: number, discipline: DisciplineType): Promise<EligibleCategory[]> {
        return promiseRequest<EligibleCategory[]>({
            url: `/api/admin/events/${eventId}/discipline-packages/${discipline}/eligible-categories`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
};
